/**
 * CCTV DIRECT UK - Unified Search Engine v2
 * ------------------------------------------------------------------
 * ONE engine powers every search surface:
 *   - PC header dropdown, mobile header, mobile drawer, search popup
 *   - The full /search results page (assets/cctv-search-page.js)
 * so the dropdown count and the results page count are ALWAYS identical.
 *
 * Data: live catalog rendered by Shopify (/collections/all?view=cctv-catalog),
 * cached in sessionStorage for 15 min; falls back to the static JSON asset.
 * Never searches product descriptions (that caused the "thermal" false hits).
 */
(function () {
  'use strict';

  if (window.CCTVSearch && window.CCTVSearch.version) return;

  var CACHE_KEY = 'cctv_search_catalog_v3';
  var CACHE_TTL = 15 * 60 * 1000;
  var PAGE_SIZE_FEED = 250;

  var catalog = null;
  var loading = false;
  var waiters = [];
  var vocab = null;

  /* ---------------------------------------------------------------
   * 1. CATALOG LOADING
   * ------------------------------------------------------------- */
  function finishLoad(items) {
    catalog = prepare(items || []);
    loading = false;
    var list = waiters.splice(0);
    for (var i = 0; i < list.length; i++) {
      try { list[i](catalog); } catch (e) { console.error(e); }
    }
    try { document.dispatchEvent(new CustomEvent('cctv:catalog-ready')); } catch (e) {}
  }

  function fetchJSON(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.text();
    }).then(function (txt) { return JSON.parse(txt); });
  }

  function loadLive() {
    var base = '/collections/all?view=cctv-catalog&page=';
    return fetchJSON(base + 1).then(function (first) {
      var items = first.items || [];
      var pages = first.pages || 1;
      if (pages <= 1) return items;
      var reqs = [];
      for (var p = 2; p <= pages; p++) reqs.push(fetchJSON(base + p));
      return Promise.all(reqs).then(function (rest) {
        for (var i = 0; i < rest.length; i++) items = items.concat(rest[i].items || []);
        return items;
      });
    });
  }

  function loadStatic() {
    var url = window.CCTV_SEARCH_CATALOG_URL || '/cdn/shop/t/15/assets/cctv-search-catalog.json';
    return fetchJSON(url);
  }

  function loadCatalog(cb) {
    if (catalog) { if (cb) cb(catalog); return; }
    if (cb) waiters.push(cb);
    if (loading) return;
    loading = true;

    try {
      var cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && cached.ts && (Date.now() - cached.ts) < CACHE_TTL && cached.items && cached.items.length) {
        finishLoad(cached.items);
        return;
      }
    } catch (e) {}

    loadLive().then(function (items) {
      if (!items || items.length < 20) throw new Error('live catalog too small');
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), items: items })); } catch (e) {}
      finishLoad(items);
    }).catch(function (err) {
      console.warn('[CCTV Search] live catalog failed, using static fallback:', err);
      loadStatic().then(finishLoad).catch(function (e2) {
        console.error('[CCTV Search] catalog unavailable', e2);
        finishLoad([]);
      });
    });
  }

  /* ---------------------------------------------------------------
   * 2. NORMALISATION & PRODUCT PREPARATION
   * ------------------------------------------------------------- */
  function norm(s) {
    return String(s || '').toLowerCase()
      .replace(/colour/g, 'color')
      .replace(/wi-fi/g, 'wifi')
      .replace(/[\u2013\u2014]/g, '-');
  }
  function compact(s) { return norm(s).replace(/[^a-z0-9]/g, ''); }

  var ACCESSORY_RE = /\b(bracket|mount|mounting|junction box|back ?box|backbox|pole|corner|pendant|adaptor|adapter|cable|connector|housing|power supply|psu|lens cap|sun ?shield|rain ?shade|rain ?shield|shield|wall plate|bezel|distributor|keyboard|joystick|controller)\b/;

  function classify(p) {
    var t = p._t, y = p._y;
    var s = t + ' ' + y;
    if (ACCESSORY_RE.test(s) && !/\bcamera kit\b|\bkit\b/.test(t)) return 'accessories';
    if (/\bkit\b|\bbundle\b/.test(s) && /camera|cctv|nvr|dvr/.test(s)) return 'kits';
    if (/thermal|heatpro|bi-spectrum/.test(s) && /camera/.test(s)) return 'cameras';
    if (/\bnvr\b|network video recorder/.test(s)) return 'nvr';
    if (/\bdvr\b|digital video recorder|turbo hd recorder/.test(s)) return 'dvr';
    if (/camera|\bptz\b|speed dome|turret|bullet|\bdome\b|eyeball|fisheye|\bipc\b|turretcam|bulletcam|domecam/.test(s)) return 'cameras';
    if (/intercom|door ?station|indoor station|doorbell|villa|door ?phone|monitor station/.test(s)) return 'intercom';
    if (/alarm|detector|sensor|siren|keypad|ajax hub|\bhub\b|panic|smoke|motionprotect|doorprotect|fireprotect|keyfob|key fob/.test(s)) return 'alarm';
    if (/hard drive|\bhdd\b|\bssd\b|sd card|microsd|memory card|storage/.test(s)) return 'storage';
    if (/switch|router|access point|\bpoe\b|ethernet|wireless bridge|network/.test(s)) return 'network';
    if (/monitor|display|screen/.test(s)) return 'monitors';
    return 'other';
  }

  var GROUP_ORDER = { cameras: 0, nvr: 1, dvr: 2, kits: 3, intercom: 4, alarm: 5, storage: 6, network: 7, monitors: 8, accessories: 9, other: 10 };
  var GROUP_LABEL = {
    cameras: 'Cameras', nvr: 'NVRs', dvr: 'DVRs', kits: 'CCTV Kits', intercom: 'Intercoms',
    alarm: 'Alarms & Sensors', storage: 'Storage', network: 'Networking', monitors: 'Monitors',
    accessories: 'Accessories', other: 'Other'
  };

  function brandOf(p) {
    var v = norm(p.v || '');
    var t = p._t;
    if (/hilook/.test(v) || /^hilook/.test(t)) return 'HiLook';
    if (/hikvision/.test(v) || /^hikvision/.test(t)) return 'Hikvision';
    if (/ajax/.test(v) || /^ajax/.test(t)) return 'Ajax';
    if (p.v) return p.v;
    var first = (p.t || '').split(' ')[0];
    return first || 'Other';
  }

  function prepare(items) {
    var out = [];
    var seen = {};
    for (var i = 0; i < items.length; i++) {
      var p = items[i];
      if (!p || !p.h || seen[p.h]) continue;
      seen[p.h] = true;
      p._t = norm(p.t);
      p._y = norm(p.y);
      p._sku = norm((p.s || '') + ' ' + (p.vs || ''));
      p._g = norm((p.g || []).join(' | '));
      p._v = norm(p.v || '');
      p._h = norm(p.h);
      p._words = p._t + ' | ' + p._y + ' | ' + p._g + ' | ' + p._v;
      p._skuC = compact(p._sku);
      p._titleC = compact(p._t);
      p._codeC = p._skuC + '|' + p._titleC + '|' + compact(p._g) + '|' + compact(p._h);
      p._price = parseFloat(p.p) || 0;
      p._group = classify(p);
      p._brand = brandOf(p);
      out.push(p);
    }
    vocab = null;
    return out;
  }

  /* ---------------------------------------------------------------
   * 3. QUERY PARSING
   * ------------------------------------------------------------- */
  var SOFT = { the: 1, a: 1, an: 1, and: 1, for: 1, with: 1, of: 1, in: 1, to: 1, on: 1, cctv: 1, security: 1, uk: 1, buy: 1, best: 1, cheap: 1, price: 1, prices: 1, '&': 1, '+': 1, '-': 1 };

  // Each key expands to alternatives; a token matches if ANY alternative matches.
  var SYN = {
    camera: ['camera', 'cam', 'turret', 'bullet', 'dome', 'ptz', 'eyeball', 'fisheye', 'ipc', 'speed dome'],
    cam: ['camera', 'cam'],
    color: ['color'],
    colorvu: ['colorvu', 'color vu'],
    doorbell: ['doorbell', 'door station', 'villa door'],
    intercom: ['intercom', 'door station', 'indoor station', 'doorbell'],
    hdd: ['hard drive', 'hdd', 'hard disk', 'harddrive'],
    ssd: ['ssd'],
    sd: ['sd card', 'microsd', 'memory card'],
    nvr: ['nvr', 'network video recorder'],
    dvr: ['dvr', 'digital video recorder'],
    recorder: ['recorder', 'nvr', 'dvr'],
    ptz: ['ptz', 'speed dome', 'pan tilt', 'pan-tilt', 'pt camera'],
    monitor: ['monitor', 'display'],
    screen: ['monitor', 'display', 'screen'],
    kit: ['kit', 'bundle'],
    bundle: ['kit', 'bundle'],
    bracket: ['bracket', 'mount'],
    mount: ['mount', 'bracket'],
    wifi: ['wifi', 'wireless', 'wlan'],
    wireless: ['wireless', 'wifi'],
    thermal: ['thermal', 'heatpro', 'bi-spectrum'],
    '4k': ['4k', '8mp', '8 mp'],
    '8mp': ['8mp', '4k', '8 mp'],
    alarm: ['alarm', 'intrusion', 'ax pro', 'hub'],
    sensor: ['sensor', 'detector', 'protect'],
    detector: ['detector', 'sensor', 'protect'],
    switch: ['switch'],
    psu: ['power supply', 'psu', 'adaptor', 'adapter'],
    junction: ['junction'],
    anpr: ['anpr', 'lpr', 'license plate', 'number plate'],
    lpr: ['anpr', 'lpr', 'license plate', 'number plate'],
    fisheye: ['fisheye', 'fish-eye', 'panoramic'],
    solar: ['solar'],
    '4g': ['4g', 'lte']
  };
  var INTENT = {
    thermal: 'cameras', ptz: 'cameras', turret: 'cameras', bullet: 'cameras', dome: 'cameras', camera: 'cameras', cam: 'cameras', fisheye: 'cameras', anpr: 'cameras', colorvu: 'cameras',
    nvr: 'nvr', dvr: 'dvr', recorder: 'nvr', kit: 'kits', bundle: 'kits', intercom: 'intercom', doorbell: 'intercom',
    alarm: 'alarm', sensor: 'alarm', detector: 'alarm', hdd: 'storage', ssd: 'storage', sd: 'storage',
    switch: 'network', monitor: 'monitors', screen: 'monitors', bracket: 'accessories', mount: 'accessories', junction: 'accessories', psu: 'accessories'
  };
  var CATEGORY_WORDS = { thermal: 1, ptz: 1, turret: 1, bullet: 1, dome: 1, nvr: 1, dvr: 1, intercom: 1, doorbell: 1, monitor: 1, kit: 1, bracket: 1, switch: 1, hdd: 1, ssd: 1, solar: 1, fisheye: 1, anpr: 1, alarm: 1, sensor: 1, detector: 1 };

  var UNIT = {
    ch: 'channel', channel: 'channel', channels: 'channel',
    port: 'port', ports: 'port', way: 'port',
    mp: 'mp', megapixel: 'mp',
    tb: 'tb', gb: 'gb',
    inch: 'inch', inches: 'inch', '"': 'inch', in: 'inch'
  };
  var UNIT_RE = {
    channel: '(?:-|\\s)?(?:channel|ch)\\b',
    port: '(?:-|\\s)?(?:port|ports|way)\\b',
    mp: '(?:-|\\s)?(?:mp|megapixel)\\b',
    tb: '(?:-|\\s)?tb\\b',
    gb: '(?:-|\\s)?gb\\b',
    inch: '(?:-|\\s)?(?:inch|in\\b|")'
  };

  function escRe(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

  function singular(w) {
    if (w.length > 4 && /ies$/.test(w)) return w.slice(0, -3) + 'y';
    if (w.length > 3 && /s$/.test(w) && !/ss$/.test(w) && !/\d/.test(w)) return w.slice(0, -1);
    return w;
  }

  function parseQuery(raw) {
    var q = norm(raw).trim()
      .replace(/hard\s+drives?|hard\s+disks?/g, 'hdd')
      .replace(/sd\s+cards?|memory\s+cards?|micro\s*sd/g, 'sd')
      .replace(/power\s+supply|power\s+supplies/g, 'psu')
      .replace(/junction\s+box(es)?/g, 'junction')
      .replace(/pan\s*[-\s]?\s*tilt/g, 'ptz')
      .replace(/speed\s+domes?/g, 'ptz')
      .replace(/door\s*bells?/g, 'doorbell')
      .replace(/number\s+plate|licen[cs]e\s+plate/g, 'anpr')
      .replace(/color\s+vu/g, 'colorvu')
      .replace(/(\d)\s*"/g, '$1 inch')
      .replace(/(\d+)\s*-\s*(channel|ch|port|way|mp)\b/g, '$1 $2');

    var raws = q.split(/[\s,;]+/).filter(Boolean);
    var tokens = [];
    for (var i = 0; i < raws.length; i++) {
      var w = raws[i].replace(/^[^a-z0-9]+|[^a-z0-9")]+$/g, '');
      if (!w || SOFT[w]) continue;

      // "8ch", "16channel", "4tb", "8mp" style single tokens
      var mm = w.match(/^(\d+(?:\.\d+)?)(ch|channel|channels|port|ports|way|tb|gb|inch)$/);
      if (mm) { tokens.push({ kind: 'measure', n: mm[1], unit: UNIT[mm[2]], raw: w }); continue; }

      // "8 channel" -> merge number + unit
      if (/^\d+(\.\d+)?$/.test(w) && i + 1 < raws.length && UNIT[raws[i + 1]] && raws[i + 1] !== 'in') {
        tokens.push({ kind: 'measure', n: w, unit: UNIT[raws[i + 1]], raw: w + ' ' + raws[i + 1] });
        i++;
        continue;
      }

      if (/\d/.test(w)) {
        var c = compact(w);
        if (!c) continue;
        if (/^\d{1,2}$/.test(c)) tokens.push({ kind: 'num', n: c, raw: w });
        else if (SYN[c]) tokens.push({ kind: 'word', alts: SYN[c], key: c, raw: w });
        else tokens.push({ kind: 'code', c: c, raw: w });
        continue;
      }

      var s = singular(w);
      var key = SYN[w] ? w : (SYN[s] ? s : null);
      var alts = key ? SYN[key].slice() : [s];
      if (!key && s !== w) alts.push(w);
      tokens.push({ kind: 'word', alts: alts, key: key || s, raw: w, cat: !!CATEGORY_WORDS[key || s] });
    }
    return tokens;
  }

  /* ---------------------------------------------------------------
   * 4. MATCHING
   * ------------------------------------------------------------- */
  var reCache = {};
  function wordRe(alt) {
    var k = 'w:' + alt;
    if (!reCache[k]) reCache[k] = new RegExp('(^|[^a-z0-9])' + escRe(alt));
    return reCache[k];
  }
  function wholeRe(alt) {
    var k = 'x:' + alt;
    if (!reCache[k]) reCache[k] = new RegExp('(^|[^a-z0-9])' + escRe(alt) + '($|[^a-z0-9])');
    return reCache[k];
  }

  function matchAlt(p, alt) {
    if (alt.indexOf(' ') !== -1 || alt.indexOf('-') !== -1) return p._words.indexOf(alt) !== -1;
    if (alt.length <= 3) return wordRe(alt).test(p._words) || wordRe(alt).test(p._sku);
    return p._words.indexOf(alt) !== -1;
  }

  function matchToken(p, tk) {
    switch (tk.kind) {
      case 'code':
        return p._codeC.indexOf(tk.c) !== -1;
      case 'num':
        var rn = reCache['n:' + tk.n] || (reCache['n:' + tk.n] = new RegExp('(^|[^0-9a-z.])' + tk.n + '([^0-9]|$)'));
        return rn.test(p._t) || rn.test(p._g);
      case 'measure':
        var key = 'm:' + tk.n + tk.unit;
        var rm = reCache[key] || (reCache[key] = new RegExp('(^|[^0-9.])' + escRe(tk.n) + UNIT_RE[tk.unit]));
        return rm.test(p._t) || rm.test(p._g) || rm.test(p._y);
      default:
        for (var i = 0; i < tk.alts.length; i++) if (matchAlt(p, tk.alts[i])) return true;
        return false;
    }
  }

  function scoreProduct(p, tokens, qC) {
    var score = 0;
    if (qC.length >= 4) {
      var skus = p._sku.split(/\s+/);
      for (var s = 0; s < skus.length; s++) {
        var sc = compact(skus[s]);
        if (!sc) continue;
        if (sc === qC) { score += 1000; break; }
        if (sc.indexOf(qC) === 0 || sc.replace(/^(ds|ids)/, '').indexOf(qC) === 0) { score += 600; break; }
      }
    }
    var inTitleAll = true;
    for (var i = 0; i < tokens.length; i++) {
      var tk = tokens[i];
      if (tk.kind === 'code') {
        if (p._skuC.indexOf(tk.c) !== -1) score += 300;
        if (p._titleC.indexOf(tk.c) !== -1) score += 120;
        else inTitleAll = false;
      } else if (tk.kind === 'word') {
        var best = 0;
        for (var a = 0; a < tk.alts.length; a++) {
          var alt = tk.alts[a];
          var w = 0;
          if (wholeRe(alt).test(p._t)) w = 70;
          else if (wordRe(alt).test(p._t)) w = 50;
          else if (p._t.indexOf(alt) !== -1) w = 30;
          if (p._y.indexOf(alt) !== -1) w += 40;
          if (p._g.indexOf(alt) !== -1) w += 15;
          if (a > 0) w = Math.round(w * 0.75);   // synonym hits rank below literal hits
          if (w > best) best = w;
        }
        if (best < 30) inTitleAll = false;
        score += best;
      } else {
        score += 60;
      }
    }
    if (inTitleAll) score += 80;

    // Boost the product family the shopper is clearly asking for
    for (var g = 0; g < tokens.length; g++) {
      var want = tokens[g].kind === 'word' ? INTENT[tokens[g].key] : null;
      if (want && want === p._group) { score += 100; break; }
    }

    // Push accessories below devices unless the shopper asked for accessories
    var wantsAccessory = false;
    for (var j = 0; j < tokens.length; j++) {
      if (tokens[j].kind === 'word' && /bracket|mount|junction|box|cable|pole|adapt|psu|power|housing|plate|accessor/.test(tokens[j].key)) wantsAccessory = true;
    }
    if (p._group === 'accessories' && !wantsAccessory) score -= 250;

    if (p.a === false) score -= 40;
    return score;
  }

  /* ---------------------------------------------------------------
   * 5. TYPO TOLERANCE (only used when the strict search finds nothing)
   * ------------------------------------------------------------- */
  function buildVocab() {
    vocab = {};
    for (var i = 0; i < catalog.length; i++) {
      var ws = (catalog[i]._t + ' ' + catalog[i]._y + ' ' + catalog[i]._g).split(/[^a-z]+/);
      for (var j = 0; j < ws.length; j++) if (ws[j].length >= 4) vocab[ws[j]] = (vocab[ws[j]] || 0) + 1;
    }
  }
  function editDistance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var prev = [], cur = [], i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      cur = [i];
      var rowMin = i;
      for (j = 1; j <= b.length; j++) {
        var cost = a[i - 1] === b[j - 1] ? 0 : 1;
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) cur[j] = Math.min(cur[j], prev[j - 2] + 1);
        if (cur[j] < rowMin) rowMin = cur[j];
      }
      if (rowMin > max) return max + 1;
      prev = cur;
    }
    return prev[b.length];
  }
  function correctWord(w) {
    if (!vocab) buildVocab();
    if (vocab[w]) return null;
    var max = w.length >= 8 ? 2 : 1;
    var best = null, bestD = max + 1, bestF = 0;
    for (var v in vocab) {
      if (v[0] !== w[0] && max === 1) continue;
      var d = editDistance(w, v, max);
      if (d < bestD || (d === bestD && vocab[v] > bestF)) { best = v; bestD = d; bestF = vocab[v]; }
    }
    return bestD <= max ? best : null;
  }

  /* ---------------------------------------------------------------
   * 6. PUBLIC SEARCH
   * ------------------------------------------------------------- */
  function runStrict(tokens, qC) {
    var hits = [];
    for (var i = 0; i < catalog.length; i++) {
      var p = catalog[i];
      var ok = true;
      for (var t = 0; t < tokens.length; t++) {
        if (!matchToken(p, tokens[t])) { ok = false; break; }
      }
      if (ok) hits.push({ p: p, s: scoreProduct(p, tokens, qC) });
    }
    hits.sort(function (a, b) {
      return (b.s - a.s) ||
        (GROUP_ORDER[a.p._group] - GROUP_ORDER[b.p._group]) ||
        (a.p.t < b.p.t ? -1 : a.p.t > b.p.t ? 1 : 0);
    });
    return hits;
  }

  /**
   * search(query) -> { products: [...all matches, ranked], total, corrected, query }
   */
  function search(query) {
    var empty = { products: [], total: 0, corrected: null, query: query || '' };
    if (!catalog || !catalog.length) return empty;
    var tokens = parseQuery(query);
    if (!tokens.length) return empty;
    var qC = compact(query);

    var hits = runStrict(tokens, qC);
    var corrected = null;

    if (!hits.length) {
      var changed = false;
      var fixedWords = [];
      for (var i = 0; i < tokens.length; i++) {
        var tk = tokens[i];
        if (tk.kind === 'word' && !tk.cat && tk.raw.length >= 4) {
          var fix = correctWord(tk.raw);
          if (fix) {
            tokens[i] = { kind: 'word', alts: [singular(fix), fix], key: fix, raw: fix };
            fixedWords.push(fix);
            changed = true;
            continue;
          }
        }
        fixedWords.push(tk.raw);
      }
      if (changed) {
        hits = runStrict(tokens, qC);
        if (hits.length) corrected = fixedWords.join(' ');
      }
    }

    var products = new Array(hits.length);
    for (var h = 0; h < hits.length; h++) products[h] = hits[h].p;
    return { products: products, total: products.length, corrected: corrected, query: query };
  }

  function searchUrl(q) {
    var term = (q || '').trim();
    if (term && !term.endsWith('*')) term += '*';
    return '/search?type=product&q=' + encodeURIComponent(term);
  }

  window.CCTVSearch = {
    version: 2,
    load: loadCatalog,
    search: search,
    searchUrl: searchUrl,
    groupLabel: function (g) { return GROUP_LABEL[g] || 'Other'; },
    groupOrder: GROUP_ORDER,
    isReady: function () { return !!catalog; }
  };

  /* ---------------------------------------------------------------
   * 7. DROPDOWN UI (header PC, mobile header, drawer, popup)
   * ------------------------------------------------------------- */
  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }
  function money(v) {
    var n = parseFloat(v);
    if (isNaN(n)) return '';
    return '£' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  function highlight(text, query) {
    var safe = esc(text);
    var words = norm(query).split(/[\s,;/]+/).filter(function (w) { return w.length >= 2 && !SOFT[w]; });
    if (!words.length) return safe;
    words.sort(function (a, b) { return b.length - a.length; });
    try {
      var re = new RegExp('(' + words.map(escRe).join('|') + ')', 'gi');
      return safe.replace(re, '<mark>$1</mark>');
    } catch (e) { return safe; }
  }

  var DROPDOWN_LIMIT = 8;
  var NO_IMG = '//cdn.shopify.com/s/assets/no-image-2048-5e88c1b20e087fb7bbe9a3771824e743c244f437e4f8ba46b2a4d15c60372368_120x120.gif';

  function setupSearchInstance(inputEl, dropdownEl) {
    if (!inputEl || !dropdownEl || inputEl.dataset.cctvSearchBound === 'true') return;
    inputEl.dataset.cctvSearchBound = 'true';
    inputEl.setAttribute('autocomplete', 'off');

    var timer = null;
    var sel = -1;

    // Make Enter / submit always land on the unified results page
    var form = inputEl.form;
    if (form && !form.dataset.cctvSearchForm) {
      form.dataset.cctvSearchForm = 'true';
      form.addEventListener('submit', function (e) {
        var v = (inputEl.value || '').trim();
        if (!v) { e.preventDefault(); return; }
        e.preventDefault();
        window.location.href = searchUrl(v);
      });
    }

    function open() { dropdownEl.style.display = 'flex'; dropdownEl.classList.add('is-open'); }
    function close() {
      dropdownEl.style.display = 'none';
      dropdownEl.classList.remove('is-open');
      dropdownEl.innerHTML = '';
      sel = -1;
    }

    function renderLoading() {
      dropdownEl.innerHTML = '<div class="cctv-search-loading"><div class="cctv-search-spinner"></div><span>Searching products...</span></div>';
      open();
    }

    function render(q) {
      var res = search(q);
      var items = res.products.slice(0, DROPDOWN_LIMIT);
      var total = res.total;
      sel = -1;

      if (!total) {
        dropdownEl.innerHTML =
          '<div class="cctv-search-no-results">' +
            '<div class="cctv-search-empty-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg></div>' +
            '<div class="cctv-search-empty-title">No products found for "<strong>' + esc(q) + '</strong>"</div>' +
            '<div class="cctv-search-empty-hint">Try a model number (e.g. <code>2346</code>), a camera type (<code>turret</code>, <code>ptz</code>) or a brand (<code>Hikvision</code>).</div>' +
          '</div>';
        open();
        return;
      }

      var tc = window.CCTV_TRADE_CONFIG || {
        guestLock: false,
        tradePricingEnabled: true,
        isLoggedIn: false,
        isTrade: false,
        tradeDiscountPercent: 0,
        loginUrl: '/account/login',
        registerTradeUrl: '/pages/trade-account-application'
      };

      var tradeBanner = '';
      if (tc.guestLock && !tc.isTrade) {
        tradeBanner = '<div class="cctv-search-trade-banner cctv-search-trade-banner--guest">' +
          '<svg class="cctv-banner-lock-svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>' +
          '<span>Trade pricing is locked for guests. <a href="' + (tc.loginUrl || '/account/login') + '">Log In</a> or <a href="' + (tc.registerTradeUrl || '/pages/trade-account-application') + '">Apply for Trade Account</a></span>' +
        '</div>';
      } else if (tc.isTrade && tc.tradePricingEnabled) {
        tradeBanner = '<div class="cctv-search-trade-banner cctv-search-trade-banner--trade">' +
          '<svg class="cctv-banner-check-svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>' +
          '<span>Trade Account Active &bull; <strong>' + (tc.tradeDiscountPercent || 5) + '% Wholesale Discount Applied</strong></span>' +
        '</div>';
      }

      var html = tradeBanner + '<div class="cctv-search-results-header"><span>' +
        (res.corrected ? 'Showing results for "<strong>' + esc(res.corrected) + '</strong>"' : 'Matching Products') +
        '</span><span class="cctv-search-count-badge">' + total + ' ' + (total === 1 ? 'result' : 'results') + '</span></div>';

      html += '<ul class="cctv-search-results-list" role="listbox">';
      for (var i = 0; i < items.length; i++) {
        var p = items[i];
        var url = '/products/' + encodeURIComponent(p.h);

        var vClean = (p.v || '').toLowerCase();
        var isHiLook = vClean.indexOf('hilook') !== -1 || (p.t && p.t.toLowerCase().indexOf('hilook') !== -1);
        var isVendorLocked = vClean.indexOf('hikvision') !== -1 || vClean.indexOf('ajax') !== -1;
        var isOffer = !!p.o;
        var isGated = tc.guestLock && !tc.isTrade && isVendorLocked;

        var priceHtml = '';
        if (isGated) {
          priceHtml = '<div class="cctv-search-item-price cctv-search-price--locked">' +
            '<span class="cctv-search-lock-pill">' +
              '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>' +
              'Trade Only' +
            '</span>' +
            '<span class="cctv-search-lock-sub">' + (tc.isLoggedIn ? 'Account pending' : 'Login for price') + '</span>' +
          '</div>';
        } else if (tc.isTrade && tc.tradePricingEnabled && tc.tradeDiscountPercent > 0 && !isOffer && !isHiLook) {
          var origPrice = p._price;
          var discPrice = Math.round(origPrice * ((100 - tc.tradeDiscountPercent) / 100) * 100) / 100;
          priceHtml = '<div class="cctv-search-item-price cctv-search-price--trade">' +
            '<div class="cctv-search-price-row">' +
              '<del class="cctv-search-price-orig">' + money(p.p) + '</del>' +
              '<span class="cctv-search-price-val cctv-search-price-trade-val">' + money(discPrice) + '</span>' +
              '<span class="cctv-search-price-vat">ex VAT</span>' +
            '</div>' +
            '<span class="cctv-search-badge-trade">' + tc.tradeDiscountPercent + '% Trade</span>' +
          '</div>';
        } else if (isOffer) {
          priceHtml = '<div class="cctv-search-item-price">' +
            '<span class="cctv-search-price-val">' + money(p.p) + '</span>' +
            '<span class="cctv-search-price-vat">ex VAT</span>' +
            '<span class="cctv-search-badge-offer">OFFER</span>' +
          '</div>';
        } else {
          priceHtml = '<div class="cctv-search-item-price">' +
            '<span class="cctv-search-price-val">' + money(p.p) + '</span>' +
            '<span class="cctv-search-price-vat">ex VAT</span>' +
          '</div>';
        }

        html += '<li role="option" data-index="' + i + '"><a href="' + url + '" class="cctv-search-item">' +
          '<div class="cctv-search-item-thumb"><img src="' + esc(p.i || NO_IMG) + '" alt="' + esc(p.t) + '" loading="lazy" width="44" height="44"></div>' +
          '<div class="cctv-search-item-info"><h4 class="cctv-search-item-title">' + highlight(p.t, res.corrected || q) + '</h4>' +
          '<div class="cctv-search-item-meta">' +
            (p.s ? '<span class="cctv-search-item-sku">' + esc(p.s) + '</span>' : '') +
            '<span class="cctv-search-item-cat">' + esc(GROUP_LABEL[p._group] || p.y || '') + '</span>' +
          '</div></div>' +
          priceHtml +
        '</a></li>';
      }
      html += '</ul>';

      html += '<div class="cctv-search-footer"><a href="' + searchUrl(q) + '" class="cctv-search-view-all">' +
        '<span>View all <strong>' + total + '</strong> ' + (total === 1 ? 'result' : 'results') + ' for "<strong>' + esc(q) + '</strong>"</span>' +
        '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"></polyline></svg></a></div>';

      dropdownEl.innerHTML = html;
      open();
    }

    function run() {
      var v = (inputEl.value || '').trim();
      if (v.length < 2) { close(); return; }
      if (!catalog) {
        renderLoading();
        loadCatalog(function () { if ((inputEl.value || '').trim() === v) render(v); });
        return;
      }
      render(v);
    }

    inputEl.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(run, 90);
    });
    inputEl.addEventListener('focus', function () {
      loadCatalog();
      if ((inputEl.value || '').trim().length >= 2) run();
    });
    inputEl.addEventListener('keydown', function (e) {
      var links = dropdownEl.querySelectorAll('.cctv-search-item');
      if (e.key === 'Escape') { close(); return; }
      if (!links.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        sel = e.key === 'ArrowDown' ? (sel + 1) % links.length : (sel <= 0 ? links.length - 1 : sel - 1);
        for (var i = 0; i < links.length; i++) links[i].classList.toggle('is-selected', i === sel);
        links[sel].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter' && sel >= 0) {
        e.preventDefault();
        window.location.href = links[sel].getAttribute('href');
      }
    });
    document.addEventListener('click', function (e) {
      if (!inputEl.contains(e.target) && !dropdownEl.contains(e.target)) close();
    });
  }

  function bindContainers() {
    var containers = document.querySelectorAll('.cctv-search-wrapper, .cctv-mb-search-box, .cctv-mobile-search-wrap, .cctv-search-form, .frm_search_input');
    for (var i = 0; i < containers.length; i++) {
      var c = containers[i];
      var input = c.querySelector('.cctv-search-field, .cctv-mb-search-input, input[name="q"]');
      var dd = c.querySelector('.cctv-search-dropdown');
      if (input && dd) setupSearchInstance(input, dd);
      var form = c.matches('form') ? c : c.querySelector('form');
      if (form && !form._cctvBound) {
        form._cctvBound = true;
        form.addEventListener('submit', function (e) {
          var inp = this.querySelector('input[name="q"]');
          if (inp && inp.value) {
            var v = inp.value.trim();
            if (v && !v.endsWith('*')) inp.value = v + '*';
          }
        });
      }
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindContainers);
  else bindContainers();

  document.addEventListener('focusin', function (e) {
    var t = e.target;
    if (t && (t.name === 'q' || (t.classList && (t.classList.contains('cctv-search-field') || t.classList.contains('cctv-mb-search-input'))))) bindContainers();
  });
  document.addEventListener('shopify:section:load', bindContainers);
  window.initCctvInstantSearch = bindContainers;

  // Warm the catalog when the browser is idle
  if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(function () { loadCatalog(); }, { timeout: 3000 });
  else setTimeout(function () { loadCatalog(); }, 1500);

  try { document.dispatchEvent(new CustomEvent('cctv:search-engine-ready')); } catch (e) {}
})();
